// JSON/Firestore-safe sentinel for "no practical maximum cap".
export const NO_CAP_MAX_FEE = Number.MAX_SAFE_INTEGER;

export const CASH_SUCCESS_FEE_POLICY = {
  type: "seller_success_fee",
  chargedTo: "seller",
  deductedFrom: "seller_payout",
  rate: 0.08,
  minFee: 20,
  maxFee: NO_CAP_MAX_FEE,
  currency: "ZAR",
};

export const INSPECTION_FEE_POLICY = {
  currency: "ZAR",

  partnerDropoff: {
    type: "partner_dropoff_inspection",
    chargedTo: "none",
    amountZar: 0,
    status: "waived",
    reason: "Partner drop-off inspection is included in the seller success fee.",
  },

  platformPickup: {
    type: "platform_pickup_inspection",
    chargedTo: "seller",
    status: "payable",
    reason: "Seller pays inspection fee when choosing platform pickup.",
  },
};

export const DELIVERY_FEE_POLICY = {
  type: "buyer_delivery_fee",
  chargedTo: "buyer",
  addedTo: "buyer_payable",
  currency: "ZAR",
  reason: "Buyer pays delivery because the item must be delivered to the buyer.",
};

export function roundMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

export function normaliseFulfillmentMethod(input) {
  const raw =
    typeof input === "string"
      ? input
      : input?.fulfillmentMethod ||
        input?.method ||
        input?.fulfillmentChoice?.method ||
        input?.workflow?.fulfillmentChoice?.method ||
        input?.wf?.fulfillmentChoice?.method ||
        "";

  return String(raw || "").trim().toLowerCase();
}

export function isPartnerDropoffMethod(input) {
  const method = normaliseFulfillmentMethod(input);

  return [
    "partner_dropoff",
    "partner-dropoff",
    "approved_dropoff_point",
    "dropoff_partner",
    "partner_store_dropoff",
  ].includes(method);
}

export function isPlatformPickupMethod(input) {
  const method = normaliseFulfillmentMethod(input);

  return [
    "platform_pickup",
    "platform-pickup",
    "pickup_inspection",
    "seller_pickup",
    "platform_collection",
  ].includes(method);
}

export function calcSellerSuccessFeeBreakdown(acceptedAmount) {
  const itemAmount = roundMoney(acceptedAmount);

  if (!Number.isFinite(itemAmount) || itemAmount <= 0) {
    return {
      itemAmountZar: 0,
      buyerPayableAmountZar: 0,
      platformFeeZar: 0,
      successFeeZar: 0,
      sellerNetAmountZar: 0,
      feePolicy: CASH_SUCCESS_FEE_POLICY,
    };
  }

  const rawFee = itemAmount * CASH_SUCCESS_FEE_POLICY.rate;

  const boundedFee = Math.min(
    CASH_SUCCESS_FEE_POLICY.maxFee,
    Math.max(CASH_SUCCESS_FEE_POLICY.minFee, rawFee)
  );

  const successFee = roundMoney(Math.min(itemAmount, boundedFee));
  const sellerNet = roundMoney(Math.max(0, itemAmount - successFee));

  return {
    itemAmountZar: itemAmount,
    buyerPayableAmountZar: itemAmount,
    platformFeeZar: successFee,
    successFeeZar: successFee,
    sellerNetAmountZar: sellerNet,
    feePolicy: CASH_SUCCESS_FEE_POLICY,
  };
}

export function calcInspectionFeeBreakdown({
  fulfillmentMethod,
  configuredFeeZar = 0,
} = {}) {
  const method = normaliseFulfillmentMethod(fulfillmentMethod);

  if (isPartnerDropoffMethod(method)) {
    return {
      fulfillmentMethod: method || "partner_dropoff",
      inspectionFeeRequired: false,
      inspectionFeeStatus: "waived",
      sellerInspectionFeeZar: 0,
      payableAmountZar: 0,
      chargedTo: "none",
      reason: INSPECTION_FEE_POLICY.partnerDropoff.reason,
      feePolicy: INSPECTION_FEE_POLICY.partnerDropoff,
    };
  }

  const fee = roundMoney(configuredFeeZar);

  if (isPlatformPickupMethod(method) && fee > 0) {
    return {
      fulfillmentMethod: method || "platform_pickup",
      inspectionFeeRequired: true,
      inspectionFeeStatus: "payable",
      sellerInspectionFeeZar: fee,
      payableAmountZar: fee,
      chargedTo: "seller",
      reason: INSPECTION_FEE_POLICY.platformPickup.reason,
      feePolicy: INSPECTION_FEE_POLICY.platformPickup,
    };
  }

  return {
    fulfillmentMethod: method || "unknown",
    inspectionFeeRequired: false,
    inspectionFeeStatus: "not_required",
    sellerInspectionFeeZar: 0,
    payableAmountZar: 0,
    chargedTo: "none",
    reason: "No inspection fee is required for this route.",
    feePolicy: null,
  };
}

export function calcDeliveryFeeBreakdown({
  deliveryFeeZar = 0,
  deliveryMethod = "",
} = {}) {
  const fee = roundMoney(deliveryFeeZar);

  return {
    deliveryRequired: fee > 0,
    deliveryFeeZar: fee > 0 ? fee : 0,
    payableAmountZar: fee > 0 ? fee : 0,
    chargedTo: "buyer",
    deliveryMethod,
    feePolicy: DELIVERY_FEE_POLICY,
  };
}

export function calcCashTradeCheckoutBreakdown({
  acceptedAmount,
  fulfillmentMethod,
  deliveryFeeZar = 0,
  deliveryMethod = "",
  inspectionFeeZar = 0,
} = {}) {
  const success = calcSellerSuccessFeeBreakdown(acceptedAmount);

  const inspection = calcInspectionFeeBreakdown({
    fulfillmentMethod,
    configuredFeeZar: inspectionFeeZar,
  });

  const delivery = calcDeliveryFeeBreakdown({
    deliveryFeeZar,
    deliveryMethod,
  });

  const buyerPayableAmountZar = roundMoney(
    success.itemAmountZar + delivery.deliveryFeeZar
  );

  return {
    itemAmountZar: success.itemAmountZar,

    // Buyer pays item + delivery.
    buyerPayableAmountZar,
    buyerDeliveryFeeZar: delivery.deliveryFeeZar,

    // Seller success fee is deducted from payout.
    sellerSuccessFeeZar: success.successFeeZar,
    sellerNetAmountZar: success.sellerNetAmountZar,

    // Seller pays this only for platform pickup.
    sellerInspectionFeeRequired: inspection.inspectionFeeRequired,
    sellerInspectionFeeZar: inspection.sellerInspectionFeeZar,
    sellerInspectionPayableNowZar: inspection.payableAmountZar,
    inspectionFeeStatus: inspection.inspectionFeeStatus,
    inspectionChargedTo: inspection.chargedTo,

    fulfillmentMethod: inspection.fulfillmentMethod,

    successFeeBreakdown: success,
    inspectionFeeBreakdown: inspection,
    deliveryFeeBreakdown: delivery,
  };
}

export function isInspectionFeeCleared(status) {
  const s = String(status || "").trim().toLowerCase();
  return ["paid", "waived", "not_required", "not required"].includes(s);
}

export function formatZar(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "R0.00";

  try {
    return n.toLocaleString("en-ZA", {
      style: "currency",
      currency: "ZAR",
    });
  } catch {
    return `R${n.toFixed(2)}`;
  }
}

export function getCashOfferAmount(offer) {
  const raw =
    offer?.proposedCashAmount ??
    offer?.cashAmount ??
    offer?.amount ??
    offer?.cash ??
    null;

  const n = Number(String(raw ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
}